import { useState } from "react";
import { Check, ChevronsUpDown, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEntity } from "@/hooks/useEntity";

interface EntitySwitcherProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
}

export function EntitySwitcher({ value, onValueChange }: EntitySwitcherProps) {
  const [open, setOpen] = useState(false);
  const { entities, isLoading, hasMultipleEntities } = useEntity();

  // Don't show if only one entity or loading
  if (isLoading || !hasMultipleEntities) {
    return null;
  }

  const selectedEntity = entities.find((e) => e.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[240px] justify-between"
        >
          <Building2 className="h-4 w-4 shrink-0 opacity-50" />
          <span className="truncate">
            {selectedEntity ? selectedEntity.name : "All Entities (Consolidated)"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0">
        <Command>
          <CommandInput placeholder="Search entity..." />
          <CommandList>
            <CommandEmpty>No entity found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all-entities"
                onSelect={() => {
                  onValueChange(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "h-4 w-4",
                    value === null ? "opacity-100" : "opacity-0"
                  )}
                />
                All Entities (Consolidated)
              </CommandItem>
              {entities.map((entity) => (
                <CommandItem
                  key={entity.id}
                  value={entity.name}
                  onSelect={() => {
                    onValueChange(entity.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "h-4 w-4",
                      value === entity.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {entity.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
